import { isNonNullObject } from '@bindoc-projects/util-domain-lib';
import type {
  AddressDto,
  ClinicIdentifierDto,
  ClinicLocationsResponseDto,
  ClinicSearchResponseDto,
  SpecialtyDepartmentIdentifierDto,
  SpecialtyDepartmentResponseDto,
} from './api-response-dtos';

/**
 * Type guard for ClinicIdentifierDto
 */
export function isClinicIdentifierDto(
  obj: unknown
): obj is ClinicIdentifierDto {
  if (!isNonNullObject(obj)) {
    return false;
  }

  const hasRequiredFields = 'ik' in obj && 'sn' in obj;
  if (!hasRequiredFields) {
    return false;
  }

  const isValidIk =
    typeof obj['ik'] === 'string' || typeof obj['ik'] === 'number';
  const isValidSn =
    typeof obj['sn'] === 'string' || typeof obj['sn'] === 'number';
  const isValidYear =
    obj['year'] === undefined ||
    typeof obj['year'] === 'string' ||
    typeof obj['year'] === 'number';

  return isValidIk && isValidSn && isValidYear;
}

/**
 * Type guard for AddressDto
 */
export function isAddressDto(obj: unknown): obj is AddressDto {
  if (!isNonNullObject(obj)) {
    return false;
  }

  const isValidStreet =
    obj['street'] === undefined || typeof obj['street'] === 'string';
  const isValidPostalCode =
    obj['postalCode'] === undefined || typeof obj['postalCode'] === 'string';
  const isValidCity =
    obj['city'] === undefined || typeof obj['city'] === 'string';
  const isValidLocation =
    obj['location'] === undefined ||
    (Array.isArray(obj['location']) &&
      obj['location'].length === 2 &&
      obj['location'].every((coord: unknown) => typeof coord === 'number'));
  const isValidNote =
    obj['note'] === undefined || typeof obj['note'] === 'string';

  return (
    isValidStreet &&
    isValidPostalCode &&
    isValidCity &&
    isValidLocation &&
    isValidNote
  );
}

/**
 * Type guard for ClinicSearchResponseDto
 */
export function isClinicSearchResponseDto(
  obj: unknown
): obj is ClinicSearchResponseDto {
  if (!isNonNullObject(obj)) {
    return false;
  }

  if (!('name' in obj)) {
    return false;
  }

  // Accepts BOTH identity shapes: the flat `clinicSn` quartet (SN backend,
  // post-flattening) and the legacy nested `id` object (backends not yet
  // migrated, e.g. the edge-api-gateway khbenchgateway surface).
  const isValidIdentity =
    typeof obj['clinicSn'] === 'string' || isClinicIdentifierDto(obj['id']);
  const isValidName = typeof obj['name'] === 'string';
  const isValidAddress =
    obj['address'] === undefined || isAddressDto(obj['address']);

  return isValidIdentity && isValidName && isValidAddress;
}

/**
 * Type guard for ClinicLocationsResponseDto
 */
export function isClinicLocationsResponseDto(
  obj: unknown
): obj is ClinicLocationsResponseDto {
  if (!isNonNullObject(obj)) {
    return false;
  }

  const isValidMainLocation =
    obj['mainLocation'] === undefined ||
    obj['mainLocation'] === null ||
    isClinicIdentifierDto(obj['mainLocation']);

  const isValidChildLocations =
    obj['childLocations'] === undefined ||
    obj['childLocations'] === null ||
    (Array.isArray(obj['childLocations']) &&
      obj['childLocations'].every(isClinicIdentifierDto));

  const isValidOtherLocations =
    obj['otherLocations'] === undefined ||
    obj['otherLocations'] === null ||
    (Array.isArray(obj['otherLocations']) &&
      obj['otherLocations'].every(isClinicIdentifierDto));

  return isValidMainLocation && isValidChildLocations && isValidOtherLocations;
}

/**
 * Type guard for SpecialtyDepartmentIdentifierDto
 */
export function isSpecialtyDepartmentIdentifierDto(
  obj: unknown
): obj is SpecialtyDepartmentIdentifierDto {
  if (!isNonNullObject(obj)) {
    return false;
  }

  const hasRequiredFields = 'clinicId' in obj && 'key' in obj;
  if (!hasRequiredFields) {
    return false;
  }

  const isValidClinicId = isClinicIdentifierDto(obj['clinicId']);
  const isValidKey =
    typeof obj['key'] === 'string' || typeof obj['key'] === 'number';
  const isValidYear =
    obj['year'] === undefined || typeof obj['year'] === 'string' || typeof obj['year'] === 'number';

  return isValidClinicId && isValidKey && isValidYear;
}

/**
 * Type guard for SpecialtyDepartmentResponseDto
 */
export function isSpecialtyDepartmentResponseDto(
  obj: unknown
): obj is SpecialtyDepartmentResponseDto {
  if (!isNonNullObject(obj)) {
    return false;
  }

  const hasRequiredFields = 'id' in obj && 'name' in obj && 'fabKey' in obj;

  if (!hasRequiredFields) {
    return false;
  }

  const isValidId = isSpecialtyDepartmentIdentifierDto(obj['id']);
  const isValidName = typeof obj['name'] === 'string';
  const isValidFabKey = typeof obj['fabKey'] === 'string';
  const isValidFullyInpatientCases =
    obj['fullyInpatientCases'] === undefined ||
    typeof obj['fullyInpatientCases'] === 'number';
  const isValidSemiInpatientCases =
    obj['semiInpatientCases'] === undefined ||
    typeof obj['semiInpatientCases'] === 'number';

  return (
    isValidId &&
    isValidName &&
    isValidFabKey &&
    isValidFullyInpatientCases &&
    isValidSemiInpatientCases
  );
}
